import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';

/**
 * PDF baytları (ADR 0024 #12; mimari belge "Teknoloji": raporlar pdfmake). pdfmake'in tarayıcı
 * derlemesi ve pakete gömülü Roboto yazı tipi (Türkçe karakterli) ilk kullanımda ayrı parça
 * olarak yüklenir; gemide internetsiz çalışır, CDN yok. Dış adresten kaynak yüklenmez.
 */

interface PdfDocument {
  getStream(): Promise<{ setOpenActionAsPrint(): void }>;
  getBuffer(): Promise<Uint8Array>;
}

interface PdfMake {
  createPdf(definition: TDocumentDefinitions): PdfDocument;
  addVirtualFileSystem(vfs: Record<string, string>): void;
  setUrlAccessPolicy(callback: (url: string) => boolean): void;
}

let loader: Promise<PdfMake> | null = null;

/** CommonJS/UMD modülünün kendisi: Vite ve Node `default` altında verir. */
function moduleValue<T>(module: unknown): T {
  const value = module as { default?: unknown };
  return (value.default ?? value) as T;
}

function pdfMake(): Promise<PdfMake> {
  loader ??= Promise.all([
    import('pdfmake/build/pdfmake.js'),
    import('pdfmake/build/vfs_fonts.js'),
  ]).then(([lib, fonts]) => {
    const pdf = moduleValue<PdfMake>(lib);
    pdf.addVirtualFileSystem(moduleValue<Record<string, string>>(fonts));
    // Gemide internet yok; ofiste de dış kaynak istenmez (mimari belge §11.3)
    pdf.setUrlAccessPolicy(() => false);
    return pdf;
  });
  return loader;
}

/**
 * Belge tanımından PDF. `print`: PDF görüntüleyicide açılınca yazdırma penceresi açılır.
 */
export async function renderPdf(
  definition: TDocumentDefinitions,
  options: { print?: boolean } = {},
): Promise<Uint8Array> {
  const pdf = await pdfMake();
  const document = pdf.createPdf(definition);
  if (options.print) (await document.getStream()).setOpenActionAsPrint();
  const buffer = await document.getBuffer();
  return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}

/**
 * PDF hatasının çeviri anahtarı. Gemide PDF uygulaması yoksa (`OPEN_FAILED`) ve tarayıcı yeni
 * sekmeyi engellediyse (`POPUP_BLOCKED`) kullanıcıya ne yapacağı söylenir (ADR 0024 #20).
 */
export function pdfErrorKey(code: string): string {
  return code === 'OPEN_FAILED' || code === 'POPUP_BLOCKED'
    ? `restHours.pdf.errors.${code}`
    : 'restHours.pdf.error';
}

/** PDF'i kullanıcıya verme yolu: web ve gemi uygulaması ayrı uygular. */
export interface PdfOutput {
  /** Dosya olarak kaydettirir (indirme). */
  save(fileName: string, render: () => Promise<Uint8Array>): Promise<void>;
  /** Yazdırmaya açar. `render` yazdırma eylemli PDF'i üretir. */
  print(fileName: string, render: () => Promise<Uint8Array>): Promise<void>;
}

interface Tab {
  location: { href: string };
  close(): void;
}

/** Tarayıcı ortamı; testte sahtesi verilir. */
export interface BrowserEnv {
  open(url: string, target: string): Tab | null;
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  createAnchor(): { href: string; download: string; click(): void; remove(): void };
}

function defaultEnv(): BrowserEnv {
  return {
    open: (url, target) => window.open(url, target),
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    createAnchor: () => {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      document.body.append(anchor);
      return anchor;
    },
  };
}

const REVOKE_AFTER_MS = 60_000;

function blobOf(bytes: Uint8Array): Blob {
  return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' });
}

/**
 * Tarayıcıda indirme ve yazdırma. İndirme `<a download>` ile (Electron kaydet penceresini açar).
 * Yazdırma: pencere tıklamada, PDF üretilmeden açılır (açılır pencere engeli), sonra PDF'e gider.
 */
export function browserPdfOutput(env: BrowserEnv = defaultEnv()): PdfOutput {
  return {
    async save(fileName, render) {
      const url = env.createObjectURL(blobOf(await render()));
      const anchor = env.createAnchor();
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      anchor.remove();
      setTimeout(() => env.revokeObjectURL(url), REVOKE_AFTER_MS);
    },
    async print(_fileName, render) {
      const tab = env.open('', '_blank');
      if (!tab) throw Object.assign(new Error('POPUP_BLOCKED'), { code: 'POPUP_BLOCKED' });
      try {
        const url = env.createObjectURL(blobOf(await render()));
        tab.location.href = url;
        setTimeout(() => env.revokeObjectURL(url), REVOKE_AFTER_MS);
      } catch (error) {
        tab.close();
        throw error;
      }
    },
  };
}
